/**
 * API client for video processing endpoints
 */
// Use deployed API Gateway URL or fallback to local proxy
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://f423zq3mxf.execute-api.us-east-1.amazonaws.com/prod';


export interface ProcessRequest {
  video_s3_key: string;
  model: 'base' | 'large' | 'small';
  instance_type: string;
  export_exr: boolean;
  target_fps?: number | null;
}

export interface JobStatus {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  message: string;
  created_at: string;
  updated_at: string;
  model?: string;
  instance_type?: string;
  error?: string;
  error_category?: string;
}

export interface InstanceType {
  name: string;
  vcpus: number;
  memory_gb: number;
  gpu_type: string;
  gpu_count: number;
  estimated_cost_per_hour?: number;
}

export interface UploadResponse {
  job_id: string;
  video_s3_key: string;
  filename: string;
  file_size?: number;
}

export interface UploadUrlResponse {
  job_id: string;
  video_s3_key: string;
  upload_url: string;
  filename: string;
  expires_in: number;
}

export interface ProcessResponse {
  job_id: string;
  status: string;
  message: string;
}

export interface ResultsResponse {
  camera_data: string | null;
  depth_maps: Array<{ filename: string; url: string }>;
}

export interface PipelineInfo {
  name: string;
  display_name: string;
  license: string;
}

export interface BenchmarkRequest {
  video_s3_key: string;
  pipelines: string[];
  instance_type: string;
  export_exr: boolean;
  target_fps?: number | null;
}

export interface PipelineStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  message: string;
  time_seconds?: number;
  error?: string;
}

export interface BenchmarkJobStatus {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  message: string;
  pipeline_statuses: Record<string, PipelineStatus>;
  pipelines: string[];
  created_at: string;
  updated_at: string;
  error?: string;
  error_category?: string;
}

export interface BenchmarkPipelineResult {
  camera_data: string | null;
  depth_maps: Array<{ filename: string; url: string }>;
  status: string;
  time_seconds?: number;
  error?: string;
}

export interface BenchmarkResults {
  job_id: string;
  pipelines: Record<string, BenchmarkPipelineResult>;
}

/**
 * Safely parse a JSON response, falling back to HTTP status text
 * when the response body is not valid JSON (e.g. HTML error pages).
 */
async function parseJsonResponse(response: Response): Promise<any> {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`API error (${response.status}): ${response.statusText}`);
  }
}

/**
 * Get presigned URL for direct S3 upload (recommended for files >10MB)
 */
export async function getUploadUrl(filename: string): Promise<UploadUrlResponse> {
  const response = await fetch(`${API_BASE_URL}/api/video/upload-url?filename=${encodeURIComponent(filename)}`);

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to get upload URL');
  }

  return parseJsonResponse(response);
}

/**
 * Upload video file directly to S3 using presigned URL
 */
export async function uploadVideoToS3(file: File, uploadUrl: string): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'video/mp4',
    },
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.statusText}`);
  }
}

/**
 * Upload video file via API (for small files <10MB only)
 * For larger files, use getUploadUrl() and uploadVideoToS3()
 */
export async function uploadVideo(file: File): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/api/video/upload`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Upload failed');
  }

  return parseJsonResponse(response);
}

/**
 * Start video processing job
 */
export async function startProcessing(request: ProcessRequest): Promise<ProcessResponse> {
  const response = await fetch(`${API_BASE_URL}/api/video/process`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to start processing');
  }

  return parseJsonResponse(response);
}

/**
 * Get job status
 */
export async function getJobStatus(jobId: string): Promise<JobStatus> {
  const response = await fetch(`${API_BASE_URL}/api/video/status/${jobId}`);

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to get job status');
  }

  return parseJsonResponse(response);
}

/**
 * Get job results (download links)
 */
export async function getJobResults(jobId: string): Promise<ResultsResponse> {
  const response = await fetch(`${API_BASE_URL}/api/video/results/${jobId}`);

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to get results');
  }

  return parseJsonResponse(response);
}

/**
 * Get available GPU instance types
 */
export async function getInstanceTypes(): Promise<InstanceType[]> {
  const response = await fetch(`${API_BASE_URL}/api/video/instance-types`);

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to get instance types');
  }

  return parseJsonResponse(response);
}

/**
 * Cancel/delete job
 */
export async function cancelJob(jobId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/video/${jobId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to cancel job');
  }
}

export interface AsgStatus {
  asg_name: string;
  desired_capacity: number;
  min_size: number;
  max_size: number;
  current_instances: number;
  instance_type?: string;
  instances: Array<{
    instance_id: string;
    instance_type: string;
    state: string;
    launch_time: string;
  }>;
}

export interface AsgUpdateRequest {
  desired_capacity: number;
  instance_type?: string;
}

export interface AsgUpdateResponse {
  message: string;
  updates: string[];
  asg_name: string;
}

/**
 * Get Auto Scaling Group status
 */
export async function getAsgStatus(): Promise<AsgStatus> {
  const response = await fetch(`${API_BASE_URL}/api/video/asg/status`);

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to get ASG status');
  }

  return parseJsonResponse(response);
}

/**
 * Update Auto Scaling Group (desired capacity and/or instance type)
 */
export async function updateAsg(request: AsgUpdateRequest): Promise<AsgUpdateResponse> {
  const response = await fetch(`${API_BASE_URL}/api/video/asg/update`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to update ASG');
  }

  return parseJsonResponse(response);
}

export interface SaveTestSetRequest {
  job_id: string;
}

export interface SaveTestSetResponse {
  message: string;
  source_job_id: string;
  frame_count: number;
  total_files: number;
  staging_prefix: string;
}

export interface TestSetResponse {
  source_job_id: string;
  created_at: string;
  frame_count: number;
  total_files: number;
  video_url: string;
  camera_data_url: string;
  depth_maps: Array<{ filename: string; url: string }>;
  model?: { name: string; process_res?: number };
}

export interface TestSetV2Response {
  version: '2.0';
  source_job_id: string;
  created_at: string;
  total_files: number;
  pipelines: Record<string, {
    camera_data_url: string;
    depth_maps: Array<{ filename: string; url: string }>;
  }>;
  video_url?: string;
  model?: { name: string; process_res?: number };
}

/** Save job results to the permanent test set.
 *  The save operation is async: the API returns 202 immediately, then we
 *  poll /save-status until the background Lambda finishes copying files. */
export async function saveToTestSet(request: SaveTestSetRequest): Promise<SaveTestSetResponse> {
  // 1. Trigger the async save
  const response = await fetch(`${API_BASE_URL}/api/video/test-set/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok && response.status !== 202) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to start test set save');
  }

  // 2. Poll for completion (up to 3 minutes)
  const maxAttempts = 60; // 60 * 3s = 3 minutes
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise(resolve => setTimeout(resolve, 3000));

    const statusResp = await fetch(`${API_BASE_URL}/api/video/test-set/save-status`);
    if (!statusResp.ok) continue; // Retry on transient errors

    const status = await statusResp.json();

    if (status.status === 'completed') {
      return {
        message: 'Test set saved successfully',
        source_job_id: status.job_id,
        frame_count: status.frame_count,
        total_files: status.total_files,
        staging_prefix: status.staging_prefix,
      };
    }

    if (status.status === 'failed') {
      throw new Error(status.error || 'Save to test set failed');
    }

    // status === 'saving' — keep polling
  }

  throw new Error('Save to test set timed out after 3 minutes');
}

/** Get the current test set (returns null if no test set exists) */
export async function getTestSet(): Promise<TestSetResponse | TestSetV2Response | null> {
  const response = await fetch(`${API_BASE_URL}/api/video/test-set/retrieve`);

  if (response.status === 404) {
    return null; // No test set exists -- this is expected, not an error
  }

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to get test set');
  }

  const data = await parseJsonResponse(response);

  // Detect v2.0 format: has explicit version field or pipelines as an object (not array)
  if (data.version === '2.0' || (data.pipelines && typeof data.pipelines === 'object' && !Array.isArray(data.pipelines))) {
    return data as TestSetV2Response;
  }

  return data as TestSetResponse;
}

/**
 * Get available depth estimation pipelines for benchmarking
 */
export async function getAvailablePipelines(): Promise<PipelineInfo[]> {
  const response = await fetch(`${API_BASE_URL}/api/video/pipelines`);

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to get available pipelines');
  }

  return parseJsonResponse(response);
}

/**
 * Start a benchmark job across multiple pipelines
 */
export async function startBenchmark(request: BenchmarkRequest): Promise<ProcessResponse> {
  const response = await fetch(`${API_BASE_URL}/api/video/benchmark`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to start benchmark');
  }

  return parseJsonResponse(response);
}

/**
 * Get benchmark job status (includes per-pipeline statuses)
 */
export async function getBenchmarkStatus(jobId: string): Promise<BenchmarkJobStatus> {
  const response = await fetch(`${API_BASE_URL}/api/video/benchmark/status/${jobId}`);

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to get benchmark status');
  }

  return parseJsonResponse(response);
}

/**
 * Get benchmark results (per-pipeline results with download links)
 */
export async function getBenchmarkResults(jobId: string): Promise<BenchmarkResults> {
  const response = await fetch(`${API_BASE_URL}/api/video/benchmark/results/${jobId}`);

  if (!response.ok) {
    const error = await parseJsonResponse(response);
    throw new Error(error.detail || 'Failed to get benchmark results');
  }

  return parseJsonResponse(response);
}

